/**
 * Zod validation schemas for Sagamoon Originals (Epic 20).
 *
 * `originals_stories` is the anonymous-readable public catalog collection,
 * populated by clone-on-publish (AD-2). Characters are NOT here — they live in
 * `shared/src/data/originals-characters.ts` (AD-6).
 *
 * The document is anonymous-readable, so it stores NO publisher uid — attribution
 * is the manual `producerName` string only.
 */

import { z } from 'zod';
import { AgeBandSchema } from './common-schemas';

/**
 * One branch of an interactive choice. Mirrors the in-product `ChoicePath`
 * (`story-schemas.ts`) so the S20.4 clone transform is a 1:1 copy.
 */
export const OriginalsChoicePathSchema = z.object({
  label: z.string().min(1),
  paragraphs: z.array(z.string().min(1)).min(1),
});

/**
 * A single render block in an Originals story body.
 * - `p` — reading paragraph
 * - `reflection` — discussion-questions aside ("Keskustelukysymyksiä")
 * - `choice` — interactive choice (S20.14): prompt + two explorable paths.
 *   Mirrors the in-product `StorySection` choice so examples demonstrate the
 *   product's signature interactivity (USP #3).
 */
export const OriginalsStorySectionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('p'), text: z.string().min(1) }),
  z.object({
    type: z.literal('reflection'),
    title: z.string().min(1),
    items: z.array(z.string().min(1)).min(1),
  }),
  z.object({
    type: z.literal('choice'),
    prompt: z.string().min(1),
    pathA: OriginalsChoicePathSchema,
    pathB: OriginalsChoicePathSchema,
  }),
]);

/**
 * Snapshot of the source story's input knobs, captured at publish time.
 * Drives the "Miten tämä satu syntyi" story-parameters panel (S20.6).
 * All fields optional except the two the prototype always carries.
 */
export const OriginalsStoryParametersSchema = z.object({
  ageBand: z.string().min(1),
  theme: z.string(),
  targetAudience: z.enum(['boy', 'girl', 'any']).optional(),
  momentsType: z.string().nullable().optional(),
  episodeCount: z.number().int().positive().optional(),
  humor: z.boolean().optional(),
  suspense: z.boolean().optional(),
  includeLearning: z.boolean().optional(),
  wishesAdded: z.string().nullable().optional(),
  /** Display-only provenance (e.g. "Sagamoon Custom · v0.9"). */
  model: z.string().optional(),
  /** Display-only human timestamp (e.g. "26.4.2026 klo 21.14"). */
  generatedAt: z.string().optional(),
});

/**
 * Public Originals catalog story document. Firestore doc ID === `slug`.
 */
export const OriginalsStorySchema = z.object({
  slug: z.string().min(1),
  title: z.string().min(1).max(200),
  ageBand: AgeBandSchema,
  theme: z.string(),
  excerpt: z.string().min(1),
  readMinutes: z.number().int().positive(),
  /** Paste-in (AD-4); optional → UI falls back to gradient. */
  coverImageUrl: z.string().url().optional(),
  coverGradient: z.string().optional(),
  coverEmoji: z.string().optional(),
  body: z.array(OriginalsStorySectionSchema).min(1),
  originalPrompt: z.string().min(1),
  producerNote: z.string().optional(),
  /** Manual paste-in — publisher types their own name. Plain string, no uid. */
  producerName: z.string().min(1),
  storyParameters: OriginalsStoryParametersSchema,
  /** Practical parent tips ("Vinkkejä vanhemmille"). Optional — not all source docs carry it. */
  parentTips: z.array(z.string().min(1)).optional(),
  /** AI sequel-proposal teasers, rendered as STATIC text (no create action) on the public page. */
  sequelProposals: z.array(z.string().min(1)).optional(),
  /** Firestore Timestamp on the wire; convert to Date before validating (app convention). */
  publishedAt: z.date(),
  isActive: z.boolean(),
});

/**
 * Request payload for the `publishOriginalsStory` Cloud Function (S20.4).
 * The producer pastes these; everything else on the clone is derived from the
 * source story server-side.
 */
export const PublishOriginalsStoryRequestSchema = z.object({
  sourceStoryId: z.string().min(1),
  originalPrompt: z.string().min(1),
  producerNote: z.string().optional(),
  producerName: z.string().min(1),
  coverImageUrl: z.string().url(),
});

/**
 * Canonical Finnish consent text for the public Originals signup (S20.9).
 *
 * SINGLE SOURCE OF TRUTH (AC9): the front-end form displays this exact string,
 * and the `submitOriginalsSignup` Cloud Function stores it verbatim as the Brevo
 * `CONSENT_TEXT` attribute — so the displayed consent and the stored audit record
 * are byte-identical. Change it in one place only.
 */
export const ORIGINALS_SIGNUP_CONSENT_TEXT =
  'Hyväksyn, että Sagamoon tallentaa sähköpostini ja lähettää minulle uutiskirjeen sekä tiedon, kun testivaihe avautuu. Voin lopettaa palvelun käytön milloin tahansa.';

/**
 * Public Originals signup payload (S20.9) — the anonymous beta-waitlist form.
 * `consent` MUST be `true`: the form cannot submit without it, and the
 * `submitOriginalsSignup` Cloud Function rejects anything else BEFORE calling
 * Brevo. `firstName` is length-capped; `email` is format-validated. This is the
 * server-side contract — there is NO Firestore write (AD-5, Brevo-only).
 */
export const SubmitOriginalsSignupRequestSchema = z.object({
  firstName: z.string().trim().min(1).max(100),
  email: z.string().email(),
  consent: z.literal(true),
});

export type PublishOriginalsStoryRequest = z.infer<typeof PublishOriginalsStoryRequestSchema>;
export type OriginalsChoicePath = z.infer<typeof OriginalsChoicePathSchema>;
export type OriginalsStorySection = z.infer<typeof OriginalsStorySectionSchema>;
export type OriginalsStoryParameters = z.infer<typeof OriginalsStoryParametersSchema>;
export type OriginalsStory = z.infer<typeof OriginalsStorySchema>;
export type SubmitOriginalsSignupRequest = z.infer<typeof SubmitOriginalsSignupRequestSchema>;

/** Response from `submitOriginalsSignup`. Soft success on duplicate/pending (AC4). */
export interface SubmitOriginalsSignupResponse {
  ok: true;
}
